/**
 * cart-fly.js
 *
 * Two bits of add-to-cart feedback:
 *   1. A clone of the product photo flies from the gallery into the cart icon.
 *   2. The Add to cart button briefly swaps its label for a tick.
 *
 * Hooks into the theme's existing pub/sub: product-form.js publishes
 * PUB_SUB_EVENTS.cartUpdate with source 'product-form' once the add succeeds.
 * The submit listener runs first (capture phase) purely to remember which
 * button and image belong to the add that is about to happen.
 */
(function () {
  const FLIGHT_MS = 750;
  const TICK_MS = 1800;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let pending = null;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest && event.target.closest('form[data-type="add-to-cart-form"]');
      if (!form) return;
      pending = {
        button: form.querySelector('.product-form__submit'),
        image: findProductImage(form),
      };
    },
    true
  );

  function findProductImage(form) {
    const scope =
      form.closest('.product') || form.closest('quick-add-modal') || form.closest('.shopify-section') || document;
    return (
      scope.querySelector('.product__media-item.is-active img') ||
      scope.querySelector('.product__media-item img') ||
      scope.querySelector('.card__media img') ||
      scope.querySelector('img') ||
      null
    );
  }

  function flyToCart(img, origin) {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!img || !cartIcon || !origin || reducedMotion.matches) return;

    const originRect = origin.getBoundingClientRect();
    const to = cartIcon.getBoundingClientRect();
    if (!originRect.width || !to.width) return;

    // Launch from the Add to cart button rather than the gallery image: the
    // button is always on screen when it is pressed, whereas the photo may be
    // scrolled out of view. Size the ghost from the button's height so it
    // stays proportionate on both breakpoints.
    const size = Math.min(88, Math.max(56, originRect.height * 1.4));
    const fromLeft = originRect.left + originRect.width / 2 - size / 2;
    const fromTop = originRect.top + originRect.height / 2 - size / 2;

    const ghost = img.cloneNode(true);
    ghost.removeAttribute('id');
    ghost.removeAttribute('srcset');
    ghost.removeAttribute('sizes');
    ghost.className = 'cart-fly-ghost';
    ghost.style.left = `${fromLeft}px`;
    ghost.style.top = `${fromTop}px`;
    ghost.style.width = `${size}px`;
    ghost.style.height = `${size}px`;
    document.body.appendChild(ghost);

    const dx = to.left + to.width / 2 - (fromLeft + size / 2);
    const dy = to.top + to.height / 2 - (fromTop + size / 2);
    const endScale = Math.max(0.1, Math.min(0.45, to.width / size));

    // Arc upward slightly on the way across so it reads as a throw, not a slide.
    const animation = ghost.animate(
      [
        { transform: 'translate(0, 0) scale(0.6)', opacity: 0, offset: 0 },
        { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0.12 },
        {
          transform: `translate(${dx * 0.55}px, ${dy * 0.3 - 50}px) scale(${(1 + endScale) / 2})`,
          opacity: 0.95,
          offset: 0.6,
        },
        { transform: `translate(${dx}px, ${dy}px) scale(${endScale})`, opacity: 0.15, offset: 1 },
      ],
      { duration: FLIGHT_MS, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' }
    );

    // Tell the drawer to wait until the image has landed before sliding open.
    window.__cartFlyUntil = Date.now() + FLIGHT_MS;

    const land = () => {
      ghost.remove();
      cartIcon.classList.add('cart-icon--bump');
      setTimeout(() => cartIcon.classList.remove('cart-icon--bump'), 450);
    };
    animation.addEventListener('finish', land);
    animation.addEventListener('cancel', () => ghost.remove());
    // Safety net if the animation events never fire (backgrounded tab, etc.)
    setTimeout(() => ghost.isConnected && land(), FLIGHT_MS + 400);
  }

  function showTick(button) {
    if (!button) return;
    button.classList.add('product-form__submit--added');
    clearTimeout(button._tickTimer);
    button._tickTimer = setTimeout(() => button.classList.remove('product-form__submit--added'), TICK_MS);
  }

  function run() {
    if (!pending) return;
    const { button, image } = pending;
    pending = null;
      flyToCart(image, button);
    showTick(button);
  }

  function bind(attempt) {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      if ((attempt || 0) > 25) return;
      setTimeout(() => bind((attempt || 0) + 1), 200);
      return;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'product-form') return;
      run();
    });
  }

  bind(0);
})();
